import { RefObject, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { EnvironmentVariable } from "components/Environment/types";
import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import TableBodyRow from "ds/components/Table/Row";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import { isStickyLeft, isStickyRight } from "ds/components/TableSimple/helpers";
import useTableSimpleEffectiveColumnPinning from "ds/components/TableSimple/hooks/useEffectiveColumnPinning";
import { TableSimpleStore } from "ds/components/TableSimple/types";

import { MOUNTED_FILES_CELLS } from "../Cells";
import EnvironmentMountedFilesTableFileRow from "./FileRow";
import styles from "./styles.module.css";

type EnvironmentMountedFilesTableRowsProps = {
  data: EnvironmentVariable[];
  store: TableSimpleStore;
  tableRef: RefObject<HTMLDivElement | null>;
  activeRowId?: string;
};

const EnvironmentMountedFilesTableRows = ({
  data,
  store,
  tableRef,
  activeRowId,
}: EnvironmentMountedFilesTableRowsProps) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const { effectiveIsFirstColumnPinned } = useTableSimpleEffectiveColumnPinning(
    tableRef,
    isFirstColumnPinned
  );

  const toggleRow = (itemId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  return (
    <>
      {data.map((item) => {
        const isExpanded = expandedRows.has(item.id);
        const isActive = item.id === activeRowId;

        return [
          <TableBodyRow key={`${item.id}-file-row`} {...keyboardProps} isActive={isActive}>
            {columns.map((column, index) => {
              const Cell = MOUNTED_FILES_CELLS[column.id];

              return (
                <Cell
                  key={`${item.id}-${column.id}`}
                  id={`${item.id}-${column.id}`}
                  item={item}
                  isFirst={index === 0}
                  stickyLeft={isStickyLeft(index, effectiveIsFirstColumnPinned)}
                  stickyRight={isStickyRight(index, columns.length)}
                  onToggle={() => toggleRow(item.id)}
                  isExpanded={isExpanded}
                />
              );
            })}
          </TableBodyRow>,
          isExpanded && (
            <TableBodyRow
              key={`${item.id}-file-content-row`}
              {...keyboardProps}
              isActive={isActive}
            >
              <td
                id={`${item.id}-file-content-cell`}
                colSpan={columns.length}
                aria-colspan={columns.length}
                className={styles.fileContentCell}
              >
                <EnvironmentMountedFilesTableFileRow file={item.element} />
              </td>
            </TableBodyRow>
          ),
        ];
      })}
    </>
  );
};

export default EnvironmentMountedFilesTableRows;
